import { useCurrentUser } from '@common/current-user-layer/composables/useCurrentUser'
import { useSentry } from '@common/error-monitoring-layer/composables/useSentry'
import { watch } from 'vue'
import { getActivePinia, type Pinia } from 'pinia'
import { useGlobal } from '@common/core-layer/composables/useGlobal'
import {
  asSsrAuthEvent,
  clearAuthCookie,
  clearAuthCookieForEvent,
  getAuthCookie,
  setAuthCookie,
  type AuthCookieData,
  type ServerRequestEvent,
  type SsrAuthEvent,
  type SsrAuthEventOptions,
} from '@common/core-layer/utils/cookie.utils'
import { useNuxtApp, useRoute } from 'nuxt/app'
import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'
import { isExternalHttpsRedirect } from '@common/core-layer/utils/external-redirect.utils'
import { AuthStatus, useAuthStore } from '../store/auth.store'

import { AuthService } from '../services/auth.service'
import { OTPService } from '../services/otp.service'
import { getDeviceInfo } from '../../core-layer/utils'
import { useLocation } from '../../location-layer/composables/useLocation'
import { useLocationStore } from '../../location-layer/store/location.store'
import { MAGIC_LINK_REQUEST_SOURCE_CLIENTPORTAL_PREVIEW_V2, MigrationStatus } from '../types'
import { MigrationUtility } from '../utils/migration'
import { tryDeferredInPlacePasswordLogin } from '../utils/inPlacePasswordDefer'
import type { QueryClient } from '@tanstack/vue-query'
import type {
  AuthResponse,
  CheckPasswordSetResponse,
  UseAuthSendOtpPayload,
  UseAuthSetNewPasswordPayload,
  UseAuthSetPasswordPayload,
  UseAuthSignUpPayload,
} from '../types'

export type { AuthDeferInPlacePasswordOptions } from '../utils/inPlacePasswordDefer'

interface AuthResult {
  success: boolean
  data?: AuthResponse
  error?: string
  requiresPasswordSet?: boolean
  passwordSetContext?: {
    email: string
    contactId: string
    passwordResetToken: string
  }
  redirected?: boolean
}

interface TokenRefreshResult {
  success: boolean
  token?: string
  error?: string
}

interface SaveAuthAfterLoginOptions {
  skipPostLoginRedirect?: boolean
}

function buildAuthenticatedSetPasswordResult(
  result: AuthResult,
  data: AuthResponse,
  fallbackEmail: string | undefined,
  preflightContext?: CheckPasswordSetResponse | null
): AuthResult {
  return {
    ...result,
    success: true,
    data,
    requiresPasswordSet: true,
    passwordSetContext: {
      email: data.email || preflightContext?.email || fallbackEmail || '',
      contactId: data.contactId || preflightContext?.contactId || '',
      passwordResetToken: data.passwordResetToken || preflightContext?.passwordResetToken || '',
    },
  }
}

/** Resolve the H3 SSR event from an explicit caller or ambient Nuxt SSR context. */
function resolveSsrAuthEvent(options?: SsrAuthEventOptions): SsrAuthEvent | undefined {
  let event = options?.ssrEvent
  if (!event && process.server) {
    try {
      const nuxtApp = useNuxtApp?.()
      event = asSsrAuthEvent(nuxtApp?.ssrContext?.event)
    } catch {
      // Not in Nuxt context
    }
  }
  return event
}

/** Clear persisted auth on the client cookie or SSR response + event context. */
function clearAuthCookieForSession(event?: SsrAuthEvent): void {
  if (process.server && event?.node?.res) {
    clearAuthCookieForEvent(event)
    if (event.context?.auth) {
      delete event.context.auth
    }
    return
  }

  clearAuthCookie()
}

/**
 * Only `redirect` (and other non-credential query params) should be forwarded.
 */
const AUTH_CREDENTIAL_QUERY_KEYS = new Set(['token', 'loginCode', 'sessionKey', 'email'])

function stripAuthCredentialsFromQuery(query: Record<string, unknown>): Record<string, string> {
  const cleaned: Record<string, string> = {}
  for (const [key, value] of Object.entries(query)) {
    if (AUTH_CREDENTIAL_QUERY_KEYS.has(key)) continue
    if (value == null) continue
    if (Array.isArray(value)) {
      const first = value.find(v => typeof v === 'string') as string | undefined
      if (first) cleaned[key] = first
      continue
    }
    if (typeof value === 'string') cleaned[key] = value
  }
  return cleaned
}

export function useAuth(passedPinia?: Pinia) {
  const authStore = useAuthStore(passedPinia)

  /**
   * Get the current location ID based on platform mode
   * - GoKollab: Uses default GoKollab location data
   * - WhiteLabel: Uses location from location store
   */
  const getDefaultLocationId = (): string => {
    const { isGokollab } = useGlobal()

    if (isGokollab) {
      const { getDefaultGokollabLocationData } = useLocation()
      return getDefaultGokollabLocationData().locationId as string
    }

    const { locationId } = useLocation()
    return locationId.value as string
  }

  // Initialize services with current location
  const authService = new AuthService(getDefaultLocationId())
  const otpService = new OTPService()

  function registerAuth(token: string, userId: string, contactId: string): { success: boolean } {
    authStore.setAuth(token, userId, contactId)

    // Set Sentry user context
    try {
      const sentryHelpers = useSentry()
      sentryHelpers.setUser(userId, contactId)
    } catch {
      // Sentry not available - continue without error
    }

    return { success: true }
  }

  /**
   * Wrap `tryDeferredInPlacePasswordLogin` and, when the deferral fires, stash
   * the FULL `AuthResponse` in the auth store (`pendingAuthData`).
   *
   * SECURITY: we deliberately do NOT call {@link registerAuth} here. Doing so
   * would set `authStore.isAuthenticated`/`isLoggedIn` to `true` before the
   * user has actually set their password, letting auth-gated routes and APIs
   * accept this half-authenticated session. The `setPassword` request below
   * threads the pending token through an explicit `Authorization` header
   * instead — see {@link setPassword} — so it remains the only privileged call
   * that sees the token until the user completes the flow. The auth cookie
   * and store are only populated once {@link finalizeDeferredAuthAfterPasswordSet}
   * runs successfully; if the user reloads before completing, they remain
   * unauthenticated.
   */
  function tryDeferInPlacePasswordWithAuthRegistration(
    data: AuthResponse,
    options: AuthDeferInPlacePasswordOptions | undefined,
    emailFallback: string
  ): ReturnType<typeof tryDeferredInPlacePasswordLogin> {
    const deferred = tryDeferredInPlacePasswordLogin(data, options, emailFallback)
    if (!deferred) return null
    if (data.token && data.userId && data.contactId) {
      authStore.setPendingAuthData(data)
    }
    return deferred
  }

  /**
   * Finalize a deferred-auth login after `setPassword` succeeds. Prefers fresh
   * credentials from the set-password response; falls back to the original
   * deferred login data stashed in `authStore.pendingAuthData` when the
   * set-password response does not carry a token.
   *
   * Returns the `AuthResult` from `saveAuthAfterLogin` (so callers can honour
   * `redirected`), or null if there is no usable auth data (caller should
   * surface an error in that case).
   */
  async function finalizeDeferredAuthAfterPasswordSet(setPasswordResponse?: AuthResponse | null): Promise<AuthResult | null> {
    const fresh = setPasswordResponse?.token ? setPasswordResponse : null
    const data = fresh ?? authStore.pendingAuthData
    if (!data?.token) {
      authStore.setPendingAuthData(null)
      return null
    }
    authStore.setPendingAuthData(null)
    return await saveAuthAfterLogin(data)
  }

  /**
   * Clear authentication from store and Sentry
   */
  function deregisterAuth(): void {
    authStore.clearAuth()

    // Clear Sentry user context
    try {
      const sentryHelpers = useSentry()
      sentryHelpers.clearUser()
    } catch {
      // Sentry not available - continue without error
    }
  }

  async function saveAuthAfterLogin(data: AuthResponse, options?: SaveAuthAfterLoginOptions): Promise<AuthResult> {
    if (!data?.token) {
      throw new Error('Login failed - no token received')
    }

    // Use locationId from response, or fall back to current location
    const locationId = data.locationId || getDefaultLocationId()

    // Set auth cookie
    await setAuthCookie({
      token: data.token,
      refreshToken: data.refreshToken,
      locationId: locationId,
      userId: data.userId,
      contactId: data.contactId,
      externalUserId: data.externalUserId,
      sessionUniqueKey: data.sessionUniqueKey,
    })

    // Update auth store
    registerAuth(data.token, data.userId, data.contactId)

    // Load logged-in user profile
    try {
      const { ensureLoaded } = useCurrentUser()
      if (ensureLoaded) {
        await ensureLoaded()
      }
    } catch (e) {
      console.error('Failed to load logged-in user profile:', e)
    }

    if (process.client && (await handleMigrationGate(data))) {
      return { success: true, data, redirected: true }
    }

    if (options?.skipPostLoginRedirect) {
      return { success: true, data, redirected: false }
    }

    const didRedirect = await handleRedirectAfterLogin()

    return { success: true, data, redirected: didRedirect }
  }

  /**
   * Client-only: if the login response carries a pending migration status, navigate
   * to the matching gate page and return true. Otherwise return false so the caller
   * continues with the normal redirect flow.
   *
   * Migration state is persisted via `MigrationUtility` (localStorage) instead of
   * `history.state` so a reload, deep-link, or partial completion still keeps the
   * user on the gate. The redirect-guard middleware reads the same store.
   * Only `isGokollab` is honoured per spec — GoKollab apps do not run this flow.
   */
  async function handleMigrationGate(data: AuthResponse): Promise<boolean> {
    const { isGokollab } = useGlobal()

    if (isGokollab) {
      // Gate disabled for this app — clear any stale state from a prior session
      // and let the caller continue with the normal redirect.
      MigrationUtility.clear()
      return false
    }

    const route = useRoute()
    const { CPNavigateTo } = useCPNavigation()
    // Strip one-time auth credentials (`token` / `loginCode` / `sessionKey` / `email`)
    // before forwarding to the gate page. The auth cookie is already set at this
    // point, so leaving these in the URL would leak them into the address bar and
    // browser history of `/expired-password` and `/profile-confirmation`. Only
    // `redirect` (and any other non-credential params) should survive.
    const sanitizedQuery = stripAuthCredentialsFromQuery(route.query as Record<string, unknown>)
    const gateQuery = Object.keys(sanitizedQuery).length > 0 ? { query: sanitizedQuery } : {}

    if (data.migrationStatus === MigrationStatus.CREATED) {
      MigrationUtility.save({
        migrationStatus: MigrationStatus.CREATED,
        migrationUserId: data.userId,
        email: data.email,
        contactId: data.contactId,
        passwordResetToken: data.passwordResetToken,
      })
      await CPNavigateTo({
        name: 'auth-expired-password',
        ...gateQuery,
        options: { replace: true },
      })
      return true
    }
    if (data.migrationStatus === MigrationStatus.PASSWORD_CONFIRMED) {
      MigrationUtility.save({
        migrationStatus: MigrationStatus.PASSWORD_CONFIRMED,
        migrationUserId: data.userId,
        email: data.email,
      })
      await CPNavigateTo({
        name: 'auth-profile-confirmation',
        ...gateQuery,
        options: { replace: true },
      })
      return true
    }

    MigrationUtility.clear()
    return false
  }

  async function migrateEmail(email: string): Promise<void> {
    if (!email) return
    await MigrationUtility.migrate(email, getDefaultLocationId())
  }

  /**
   * Login with email and password.
   * @param options.deferSessionUntilInPlacePasswordSet When true and the API requires in-place Set Password, skips saveAuthAfterLogin (clientportal opt-in).
   */
  async function loginWithEmailPassword(
    email: string,
    password: string,
    captchaToken?: string,
    options?: AuthDeferInPlacePasswordOptions
  ): Promise<AuthResult> {
    try {
      authStore.setStatus(AuthStatus.LOADING)
      authStore.setError(null)

      const locationId = getDefaultLocationId()

      // Perform login
      const { data } = await authService.loginWithEmailPassword({
        email,
        password,
        locationId,
        captchaToken,
      })

      const deferred = tryDeferInPlacePasswordWithAuthRegistration(data, options, email)
      if (deferred) return deferred

      return await saveAuthAfterLogin(data)
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed'
      console.error('Login failed:', errorMessage)
      authStore.setError(errorMessage)
      throw error
    } finally {
      authStore.setStatus(authStore.isLoggedIn ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED)
    }
  }

  /**
   * Login with Google OAuth
   * @param options.deferSessionUntilInPlacePasswordSet When true and the API requires in-place Set Password, skips saveAuthAfterLogin (clientportal opt-in).
   */
  async function loginWithGoogle(googleToken: string, options?: AuthDeferInPlacePasswordOptions): Promise<AuthResult> {
    try {
      authStore.setStatus(AuthStatus.LOADING)
      authStore.setError(null)

      const { data } = await authService.loginWithSocialID({
        authProvider: 'google',
        idToken: googleToken,
        locationId: getDefaultLocationId(),
      })

      const deferred = tryDeferInPlacePasswordWithAuthRegistration(data, options, data.email ?? '')
      if (deferred) return deferred

      return await saveAuthAfterLogin(data)
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed'
      authStore.setError(errorMessage)
      throw error
    } finally {
      authStore.setStatus(authStore.isLoggedIn ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED)
    }
  }

  // Legacy login function for backward compatibility
  const login = loginWithEmailPassword

  async function generateOTPForLogin(email: string, action: string, captchaToken?: string): Promise<{ data: { otpToken: string } }> {
    try {
      authStore.setStatus(AuthStatus.LOADING)
      authStore.setError(null)

      const { data } = await otpService.generateOTPForLogin({
        email,
        action,
        captchaToken,
        locationId: getDefaultLocationId(),
      })

      return { data }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'OTP generation failed'
      authStore.setError(errorMessage)
      throw error
    } finally {
      authStore.setStatus(authStore.isLoggedIn ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED)
    }
  }

  /**
   * Verify OTP for login
   * @param options.deferSessionUntilInPlacePasswordSet When true and the API requires in-place Set Password, skips saveAuthAfterLogin (clientportal opt-in).
   */
  async function verifyOTPForLogin(
    email: string,
    action: string,
    otp: number,
    token: string,
    options?: AuthDeferInPlacePasswordOptions
  ): Promise<AuthResult> {
    try {
      authStore.setStatus(AuthStatus.LOADING)
      authStore.setError(null)

      const { data } = await authService.verifyOTPForLogin({
        email,
        action,
        otp,
        token,
        locationId: getDefaultLocationId(),
      })

      const deferred = tryDeferInPlacePasswordWithAuthRegistration(data, options, email)
      if (deferred) return deferred

      return await saveAuthAfterLogin(data)
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'OTP verification failed'
      authStore.setError(errorMessage)
      throw error
    } finally {
      authStore.setStatus(authStore.isLoggedIn ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED)
    }
  }

  /**
   * Send OTP for signup or resend
   */
  async function sendOtp(payload: UseAuthSendOtpPayload): Promise<{ data: { otpToken: string } }> {
    const { data } = await authService.sendOtp({
      email: payload.email,
      locationId: getDefaultLocationId(),
      action: payload.action,
      captchaToken: payload.captchaToken,
      type: payload.type,
      fullName: payload.fullName,
    })

    return { data }
  }

  async function signUp(payload: UseAuthSignUpPayload): Promise<AuthResult> {
    try {
      authStore.setStatus(AuthStatus.LOADING)
      authStore.setError(null)

      const { data } = await authService.signUp({
        email: payload.email,
        password: payload.password,
        username: payload.username,
        locationId: getDefaultLocationId(),
        otp: payload.otp,
        otpToken: payload.otpToken,
        redirectUrl: payload.redirectUrl,
        captchaToken: payload.captchaToken,
        referredBy: payload.referredBy,
        signupPageUrl: payload.signupPageUrl,
      })

      const result = await saveAuthAfterLogin(data)
      return result
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Signup failed'
      authStore.setError(errorMessage)
      throw error
    } finally {
      authStore.setStatus(authStore.isLoggedIn ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED)
    }
  }

  async function setPassword(payload: UseAuthSetPasswordPayload): Promise<{ data: AuthResponse }> {
    // For deferred-auth flows the user is NOT yet `authStore.isAuthenticated`,
    // so `BaseService` won't attach an Authorization header. Pass the pending
    // login token explicitly here — and ONLY here — so this single endpoint
    // sees it without granting full session access elsewhere in the app.
    const pendingToken = !authStore.isAuthenticated && authStore.pendingAuthData?.token ? authStore.pendingAuthData.token : undefined

    const { data } = await authService.setPassword(
      {
        passwordResetToken: payload.passwordResetToken,
        contactId: payload.contactId,
        newPassword: payload.newPassword,
        confirmPassword: payload.confirmPassword,
        locationId: getDefaultLocationId(),
        captchaToken: payload.captchaToken,
      },
      pendingToken ? { authToken: pendingToken } : undefined
    )

    return { data }
  }

  async function setNewPassword(payload: UseAuthSetNewPasswordPayload): Promise<{ data: AuthResponse }> {
    const { data } = await authService.setNewPassword({
      locationId: getDefaultLocationId(),
      emailId: payload.emailId,
      newPassword: payload.newPassword,
      confirmPassword: payload.confirmPassword,
      loginCode: payload.loginCode,
      token: payload.token,
    })

    return { data }
  }

  async function preflightCheckPassword(params: {
    email?: string
    userId?: string
    loginCode?: string
    token?: string
  }): Promise<CheckPasswordSetResponse | null> {
    try {
      const { data } = await authService.checkIfPasswordSet({
        locationId: getDefaultLocationId(),
        email: params.email,
        userId: params.userId,
        loginCode: params.loginCode,
        token: params.token,
      })
      return data
    } catch (error: unknown) {
      console.warn('[useAuth] checkIfPasswordSet failed; proceeding to login anyway:', error)
      return null
    }
  }

  async function loginWithLoginCode(
    email: string | undefined,
    loginCode: string,
    redirectUrl?: string | null,
    externalUserId?: string
  ): Promise<AuthResult> {
    try {
      authStore.setStatus(AuthStatus.LOADING)
      authStore.setError(null)
      const locationId = getDefaultLocationId()
      const deviceInfo = getDeviceInfo()

      let resolvedEmail = email
      let requiresPasswordSet = false
      let passwordSetPreflightContext: CheckPasswordSetResponse | null = null
      // The /auth forwarding flow allows emailless ?loginCode= URLs (the
      // backend resolves the user from the code). The preflight still runs so
      // we can preserve the SetPassword gate, but the login API must still run
      // first to exchange the code for the bearer used by SetPassword.
      if (email || externalUserId || loginCode) {
        const check = await preflightCheckPassword({ email, userId: externalUserId, loginCode })
        if (check?.email) resolvedEmail = check.email
        if (check && !check.isPasswordSet) {
          requiresPasswordSet = true
          passwordSetPreflightContext = check
        }
      }

      const { data } = await authService.loginWithLoginCode({
        email: resolvedEmail,
        loginCode,
        locationId,
        domainName: deviceInfo.domainName,
        redirectUrl: redirectUrl ?? null,
      })
      const result = await saveAuthAfterLogin(data, { skipPostLoginRedirect: requiresPasswordSet })
      if (requiresPasswordSet && !result.redirected) {
        return buildAuthenticatedSetPasswordResult(result, data, resolvedEmail, passwordSetPreflightContext)
      }
      return result
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed'
      authStore.setError(errorMessage)
      throw error
    } finally {
      authStore.setStatus(authStore.isLoggedIn ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED)
    }
  }

  /**
   * Login with magic link token.
   *
   * Pre-flight: when a `token` (or external `userId`) is present we call
   * `checkIfPasswordSet` to:
   *   1. fail fast on `isTokenExpired === true` — throw so the host page
   *      (`/auth`) renders the "tokenExpired" panel without a wasted login
   *      round-trip
   *   2. resolve the canonical email server-side
   *   3. detect users who have NOT set a password yet — still run the magic-link
   *      login API first so the CAT v2 bearer is persisted before SetPassword
   *      calls the protected endpoint.
   */
  async function loginWithMagicLink(
    email: string | undefined,
    token: string,
    redirectUrl?: string | null,
    externalUserId?: string
  ): Promise<AuthResult> {
    try {
      authStore.setStatus(AuthStatus.LOADING)
      authStore.setError(null)
      const locationId = getDefaultLocationId()
      const deviceInfo = getDeviceInfo()

      let resolvedEmail = email
      let requiresPasswordSet = false
      let passwordSetPreflightContext: CheckPasswordSetResponse | null = null
      if (token || externalUserId) {
        const check = await preflightCheckPassword({ token, userId: externalUserId })
        if (check?.isTokenExpired) {
          throw new Error('Magic link token expired')
        }
        if (check?.email) resolvedEmail = check.email
        if (check && !check.isPasswordSet) {
          requiresPasswordSet = true
          passwordSetPreflightContext = check
        }
      }

      const { data } = await authService.loginWithMagicLink({
        email: resolvedEmail,
        token,
        locationId,
        domainName: deviceInfo.domainName,
        redirectUrl: redirectUrl ?? null,
      })
      const result = await saveAuthAfterLogin(data, { skipPostLoginRedirect: requiresPasswordSet })
      if (requiresPasswordSet && !result.redirected) {
        return buildAuthenticatedSetPasswordResult(result, data, resolvedEmail, passwordSetPreflightContext)
      }
      return result
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed'
      authStore.setError(errorMessage)
      throw error
    } finally {
      authStore.setStatus(authStore.isLoggedIn ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED)
    }
  }

  /**
   * Login with session key (URL-based SSO).
   */
  async function loginWithSessionKey(email: string | undefined, sessionKey: string, redirectUrl?: string | null): Promise<AuthResult> {
    try {
      authStore.setStatus(AuthStatus.LOADING)
      authStore.setError(null)
      const locationId = getDefaultLocationId()
      const deviceInfo = getDeviceInfo()
      const { data } = await authService.loginWithSessionKey({
        email,
        sessionKey,
        locationId,
        domainName: deviceInfo.domainName,
        redirectUrl: redirectUrl ?? null,
      })
      return await saveAuthAfterLogin(data)
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed'
      authStore.setError(errorMessage)
      throw error
    } finally {
      authStore.setStatus(authStore.isLoggedIn ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED)
    }
  }

  async function performMagicLinkLoginServer(params: {
    email?: string
    loginCode?: string
    token?: string
    sessionKey?: string
    locationId: string
    domainName: string
    event: ServerRequestEvent
  }): Promise<AuthResponse> {
    const { email, loginCode, token, sessionKey, locationId, domainName, event } = params
    const hasSessionKey = sessionKey !== undefined && sessionKey !== ''
    const hasLoginCode = loginCode !== undefined && loginCode !== ''
    let data: AuthResponse
    if (hasSessionKey) {
      ;({ data } = await authService.loginWithSessionKey({ email, sessionKey: sessionKey!, locationId, domainName, redirectUrl: null }))
    } else if (hasLoginCode) {
      ;({ data } = await authService.loginWithLoginCode({ email, loginCode: loginCode!, locationId, domainName, redirectUrl: null }))
    } else {
      ;({ data } = await authService.loginWithMagicLink({ email, token: token!, locationId, domainName, redirectUrl: null }))
    }

    if (!data?.token || !data?.contactId) {
      throw new Error('Login failed - no token received')
    }

    await setAuthCookie(
      {
        token: data.token,
        refreshToken: data.refreshToken,
        locationId: data.locationId,
        userId: data.userId,
        contactId: data.contactId,
        externalUserId: data.externalUserId,
        sessionUniqueKey: data.sessionUniqueKey,
      },
      {},
      event
    )

    return data
  }

  /**
   * Send forgot password email
   */
  async function forgotPassword(email: string, captchaToken?: string): Promise<{ data: { success: boolean } }> {
    const { data } = await authService.forgotPassword({
      email,
      locationId: getDefaultLocationId(),
      captchaToken,
    })

    return { data }
  }

  async function requestMagicLink(token: string): Promise<{ data: { success: boolean } }> {
    return authService.requestMagicLink({
      token,
      locationId: getDefaultLocationId(),
      source: MAGIC_LINK_REQUEST_SOURCE_CLIENTPORTAL_PREVIEW_V2,
    })
  }

  /**
   * Validate reset password link (optional, for pre-submit validation)
   */
  async function validateResetLink(userId: string, token: string): Promise<{ data: { success: boolean } }> {
    const { data } = await authService.validateResetLink({
      userId,
      token,
      locationId: getDefaultLocationId(),
    })
    return { data }
  }

  /**
   * Reset password with userId and token from reset link
   */
  async function resetPassword(
    userId: string,
    token: string,
    password: string,
    revokeAllSessions?: boolean
  ): Promise<{ data: { success: boolean } }> {
    const { data } = await authService.resetPassword({
      userId,
      token,
      password,
      locationId: getDefaultLocationId(),
      revokeAllSessions,
    })
    return { data }
  }

  async function cleanupClientStateAfterLogout(): Promise<void> {
    if (!process.client) return

    const queryClient = useNuxtApp()?.$queryClient as QueryClient | undefined
    try {
      // Reset active queries so mounted UI gets a fresh logged-out fetch cycle.
      await queryClient?.resetQueries({ type: 'active' })
    } catch (error) {
      console.warn('Failed to reset active queries during logout:', error)
    }

    const pinia = getActivePinia()
    if (pinia && '_s' in pinia) {
      const storeMap = (
        pinia as Pinia & {
          _s: Map<
            string,
            {
              $id?: string
              $reset?: () => void
              reset?: () => void
              resetState?: () => void
              clear?: () => void
              clearState?: () => void
            }
          >
        }
      )._s
      storeMap.forEach((store, storeId) => {
        // Preserve location context so domain/location config survives logout.
        if (storeId === 'location' || store.$id === 'location') {
          return
        }
        try {
          // Prefer built-in Pinia reset for options stores.
          if (typeof store.$reset === 'function') {
            store.$reset()
            return
          }

          // Fallback for setup-style stores that expose custom reset/clear actions.
          if (typeof store.reset === 'function') {
            store.reset()
            return
          }
          if (typeof store.resetState === 'function') {
            store.resetState()
            return
          }
          if (typeof store.clearState === 'function') {
            store.clearState()
            return
          }
          if (typeof store.clear === 'function') {
            store.clear()
          }
        } catch {
          // Ignore store-specific reset failures and continue with remaining stores.
        }
      })
    }

    try {
      // Remove inactive queries to drop stale cache entries no longer observed.
      queryClient?.removeQueries({ type: 'inactive' })
    } catch (error) {
      console.warn('Failed to remove inactive queries during logout:', error)
    }
  }

  async function logout(options?: SsrAuthEventOptions): Promise<AuthResult> {
    try {
      authStore.setStatus(AuthStatus.LOADING)
      const event = resolveSsrAuthEvent(options)

      if (process.client) {
        const cookieData = getAuthCookie()
        if (cookieData?.locationId && cookieData?.sessionUniqueKey) {
          const { isAuthV2Enabled } = useLocationStore()
          try {
            await authService.updateSession({
              locationId: cookieData.locationId,
              sessionUniqueKey: cookieData.sessionUniqueKey,
              isV2: isAuthV2Enabled,
            })
          } catch (error) {
            console.error('Failed to update session during logout:', error)
          }
        }
      }

      clearAuthCookieForSession(event)

      // Clear auth store
      deregisterAuth()

      // Drop any persisted migration gate state so the next user / session starts
      // clean and the redirect guard doesn't bounce them onto a stale gate.
      MigrationUtility.clear()

      await cleanupClientStateAfterLogout()

      return { success: true }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Logout failed'
      authStore.setError(errorMessage)
      console.error('Logout failed:', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      authStore.setStatus(authStore.isLoggedIn ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED)
    }
  }

  /**
   * Check if user is authenticated (from cookie)
   */
  async function checkAuth(): Promise<AuthResult> {
    try {
      authStore.setStatus(AuthStatus.LOADING)

      // Only read cookies on client side
      if (process.server) {
        return { success: false }
      }

      const authData = getAuthCookie()

      // Token and contactId are required
      if (!authData?.token || !authData?.contactId) {
        deregisterAuth()
        return { success: false }
      }

      // Use userId if available, otherwise fallback to contactId
      const userId = authData.userId || authData.contactId
      registerAuth(authData.token, userId, authData.contactId)

      return { success: true }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Authentication check failed'
      console.error('[checkAuth] Authentication check failed:', errorMessage)
      deregisterAuth()
      authStore.setError(errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      authStore.setStatus(authStore.isLoggedIn ? AuthStatus.AUTHENTICATED : AuthStatus.UNAUTHENTICATED)
    }
  }

  /**
   * Refresh authentication token.
   *
   * SSR fallback: callers invoked from nitro handlers outside composable setup
   * (where `useNuxtApp()` throws) may pass `options.ssrEvent` — the H3 request
   * event — directly. It is used preferentially over the ambient nuxtApp for
   * reading `event.context.auth` (populated by the server auth middleware),
   * writing the refreshed `Set-Cookie` header via `setAuthCookie`, and clearing
   * auth on failure via `logout({ ssrEvent })`.
   */
  async function refreshToken(options?: SsrAuthEventOptions): Promise<TokenRefreshResult> {
    const event = resolveSsrAuthEvent(options)

    try {
      // Server: read auth from event context (set by middleware); client: read from cookie.
      let catPayload: AuthCookieData | null = null
      if (process.server && event?.context?.auth) {
        catPayload = event.context.auth
      } else {
        catPayload = getAuthCookie()
      }

      // Missing auth payload — return early without logout (nothing persisted to clear).
      if (!catPayload?.token || !catPayload?.contactId || !catPayload?.userId) {
        console.warn('[refreshToken] No auth data found')
        return { success: false, error: 'No auth data found' }
      }

      if (!catPayload.refreshToken) {
        console.warn('[refreshToken] Missing refreshToken')
        return { success: false, error: 'No refresh token found' }
      }

      // Always use current portal location from the location store (not cookie), so refresh
      // matches the active sub-account / white-label context.
      const locationId = getDefaultLocationId()
      if (!locationId) {
        console.warn('[refreshToken] No locationId from useLocation')
        await logout(event ? { ssrEvent: event } : undefined)
        return { success: false, error: 'No location id' }
      }

      // Refresh token
      const { data } = await authService.refreshToken({
        refreshToken: catPayload.refreshToken,
        locationId,
      })

      if (!data?.token) {
        throw new Error('Token refresh failed')
      }

      // Update auth cookie (align persisted locationId with the value used for refresh)
      const updatedPayload = {
        ...catPayload,
        token: data.token,
        refreshToken: data.refreshToken || catPayload.refreshToken,
        locationId,
      }

      await setAuthCookie(updatedPayload, {}, event)

      // Server: keep event.context.auth in sync for same-request access
      if (process.server && event?.context) {
        event.context.auth = updatedPayload
      }

      // Update store
      registerAuth(data.token, catPayload.userId, catPayload.contactId)

      return { success: true, token: data.token }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Token refresh failed'
      console.error('Token refresh failed:', errorMessage)

      await logout(event ? { ssrEvent: event } : undefined)

      return { success: false, error: errorMessage }
    }
  }

  function setupTokenRefresh(): (() => void) | undefined {
    // Client-only: setInterval and watch require browser
    if (!process.client) return

    const REFRESH_INTERVAL_MS = 23 * 60 * 60 * 1000 // 23 hours

    const interval = setInterval(async () => {
      if (authStore.isLoggedIn && authStore.token) {
        try {
          await refreshToken()
        } catch {
          await logout()
        }
      }
    }, REFRESH_INTERVAL_MS)

    // Clear interval when user logs out
    watch(
      () => authStore.isLoggedIn,
      (authenticated: boolean) => {
        if (!authenticated) {
          clearInterval(interval)
        }
      }
    )

    return () => clearInterval(interval)
  }

  function clearError(): void {
    authStore.setError(null)
  }

  /**
   * Guard helper for actions that require an authenticated user.
   * Opens login modal and returns false when user is not authenticated.
   */
  function ensureAuthenticated(page: string = 'LOGIN'): boolean {
    if (authStore.isLoggedIn) {
      return true
    }

    authStore.openLoginModal(page)
    return false
  }

  /**
   * Navigate to a URL. Handles external https, internal paths (/), or no-op when url is absent.
   */
  async function navigateToUrl(url: string): Promise<boolean> {
    try {
      if (isExternalHttpsRedirect(url)) {
        // window exists only in the browser; avoid ReferenceError during SSR
        if (process.client) {
          window.location.replace(url)
          return true
        }
        return false
      }

      if (url.startsWith('/')) {
        await navigateTo(url, { replace: true })
        return true
      }
      return false
    } catch (error) {
      console.error('Error navigating to URL:', error)
      throw error
    }
  }

  /**
   * Redirect after login using query.redirect when present.
   * Returns true when a navigation was issued so callers can avoid a follow-up
   * fallback redirect that would otherwise clobber the intended destination.
   */
  async function handleRedirectAfterLogin(): Promise<boolean> {
    const route = useRoute()
    const redirect = (route.query.redirect as string) || null
    if (!redirect) {
      return false
    }
    return await navigateToUrl(redirect)
  }

  async function generateIAMTokenFromFirebaseToken(
    firebaseAuthToken: string,
    cookieData?: { locationId: string; userId: string; contactId: string; externalUserId: string; sessionUniqueKey: string }
  ): Promise<{ token: string; refreshToken: string }> {
    const data = await authService.generateIAMTokenFromFirebaseToken(firebaseAuthToken)

    if (data.token && cookieData) {
      await saveAuthAfterLogin({
        token: data.token,
        refreshToken: data.refreshToken,
        locationId: cookieData.locationId,
        userId: cookieData.userId,
        contactId: cookieData.contactId,
        externalUserId: cookieData.externalUserId,
        sessionUniqueKey: cookieData.sessionUniqueKey,
      })
    }

    return data
  }

  return {
    // Store
    store: authStore,

    // Auth state management
    registerAuth,

    // Login methods
    login,
    loginWithEmailPassword,
    loginWithGoogle,
    loginWithLoginCode,
    loginWithMagicLink,
    loginWithSessionKey,
    performMagicLinkLoginServer,

    // OTP methods
    generateOTPForLogin,
    verifyOTPForLogin,
    sendOtp,

    // Signup
    signUp,

    // Password methods
    setPassword,
    setNewPassword,
    forgotPassword,
    requestMagicLink,
    validateResetLink,
    resetPassword,
    finalizeDeferredAuthAfterPasswordSet,

    // Session management
    logout,
    checkAuth,
    refreshToken,
    setupTokenRefresh,
    generateIAMTokenFromFirebaseToken,

    // Utilities
    clearError,
    ensureAuthenticated,
    navigateToUrl,
    handleRedirectAfterLogin,
    /**
     * Normal (post-login) flows do NOT need to call this — it's
     * already invoked inside the login methods above.
     */
    saveAuthAfterLogin,

    migrateEmail,
  }
}
